import { useEffect, useState } from 'react';
import { useS3Url } from './useS3Url';
import { isBrowserRenderableImage, isImageMimeType, isServerThumbnailableImage } from './assetPreview';
import { getAssetThumbnailUrl } from './store/asset.service';

/**
 * Resolves the URL to paint into an <img> for an image asset, hiding the difference between
 * the two sources.
 *
 * Formats the browser decodes itself are served straight from the original object. Formats it
 * cannot decode (TIFF, HEIC) are served from a server-generated thumbnail, requested lazily and
 * cached in S3 by the backend, so the grid never points an <img> at bytes it cannot paint.
 * Returns `null` while a request is in flight and for anything with no viewable form at all —
 * callers fall back to their own placeholder on `null`.
 */
export function useAssetPreviewUrl(
  assetId: string | null | undefined,
  fileType: string | null | undefined,
  objectKey: string | null | undefined,
): string | null {
  /** Video and audio are played by the browser from the original object, so only images
   *  are ever routed through a generated thumbnail. */
  const servedFromOriginal = !isImageMimeType(fileType) || isBrowserRenderableImage(fileType);
  const directUrl = useS3Url(servedFromOriginal ? objectKey : null);

  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const needsServerThumbnail = !servedFromOriginal && isServerThumbnailableImage(fileType) && Boolean(assetId);

  useEffect(() => {
    if (!needsServerThumbnail || !assetId) {
      setThumbnailUrl(null);
      return;
    }

    let cancelled = false;
    getAssetThumbnailUrl(assetId)
      .then((res) => {
        if (!cancelled && res?.code === 200 && res?.data?.url) {
          setThumbnailUrl(res.data.url);
        }
      })
      .catch(() => {
        if (!cancelled) setThumbnailUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [assetId, needsServerThumbnail]);

  return servedFromOriginal ? directUrl : thumbnailUrl;
}
